import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'react-feather'
import { useOrderBookDepth, formatPrice, OrderBookDepthData } from '../../hooks/useOrderBook'
import { SectionTitle } from './styleds'
import { RowBetween } from '../../components/Row'
import styled from 'styled-components'
import { MUD_DECIMALS } from '../../constants/orderbook'

const TitleRow = styled(RowBetween)`
  margin: 12px 0 8px;
  align-items: center;
`

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text2};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.bg2};
    color: ${({ theme }) => theme.text1};
  }
`

const OrderBookWrap = styled.div`
  padding: 0 0 0.75rem;
`

const OrderBookHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 12px;
  color: ${({ theme }) => theme.text2};
`

const OrderBookTable = styled.div`
  font-size: 13px;
`

const OrderBookRow = styled.div<{ depthPercent?: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 4px 0;
  min-height: 24px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: ${({ depthPercent = 0 }) => 100 - Math.min(100, depthPercent)}%;
    z-index: 0;
    pointer-events: none;
  }
`

const AskRow = styled(OrderBookRow)`
  &::before {
    background: rgba(255, 82, 82, 0.12);
  }
`

const BidRow = styled(OrderBookRow)`
  &::before {
    background: rgba(24, 144, 255, 0.12);
  }
`

const PriceCell = styled.span<{ isAsk?: boolean }>`
  position: relative;
  z-index: 1;
  font-weight: 500;
  color: ${({ isAsk }) => (isAsk ? '#ff5252' : '#1890ff')};
`

const AmountCell = styled.span`
  position: relative;
  z-index: 1;
  color: ${({ theme }) => theme.text1};
  font-weight: 500;
`

const CurrentPriceWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
`

const CurrentPriceValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.text1};
`

const VolumeBarWrap = styled.div`
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 12px;
  background: ${({ theme }) => theme.bg3};
`

const VolumeBarBuy = styled.div<{ percent: number }>`
  height: 100%;
  width: ${({ percent }) => percent}%;
  background: rgba(24, 144, 255, 0.6);
`

const VolumeBarSell = styled.div<{ percent: number }>`
  height: 100%;
  width: ${({ percent }) => percent}%;
  background: rgba(255, 82, 82, 0.5);
`

const VolumeBarLegend = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.text2};
`

const DISPLAY_LEVELS = 20
const USDT_DECIMALS = 6

function formatAmountShort(amountRaw: string): string {
  const num = parseFloat(amountRaw) / Math.pow(10, MUD_DECIMALS)
  if (num === 0) return '0'
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(2) + 'K'
  if (num < 0.01) return '<0.01'
  return num.toFixed(2)
}

function OrderBookContent({ data }: { data: OrderBookDepthData }) {
  const { t } = useTranslation()
  // asks 按价格升序，取前 N 档（最接近市价），反转后显示：高价在上、低价在下，5.0 紧贴市价行上方
  const asks = data.asks.slice(0, DISPLAY_LEVELS).reverse()
  const bids = data.bids.slice(0, DISPLAY_LEVELS)
  const maxAsk = asks.length
    ? Math.max(...asks.map(a => parseFloat(a.amount)))
    : 1
  const maxBid = bids.length
    ? Math.max(...bids.map(b => parseFloat(b.amount)))
    : 1

  const currentPriceNum = parseFloat(data.currentPrice) / Math.pow(10, USDT_DECIMALS)

  return (
    <OrderBookWrap>
      <OrderBookHeader>
        <span>{t('orderBookPrice')}</span>
        <span>{t('orderBookQuantity')}</span>
      </OrderBookHeader>

      <OrderBookTable>
        {asks.map((level, i) => (
          <AskRow
            key={`ask-${level.price}-${i}`}
            depthPercent={maxAsk > 0 ? (parseFloat(level.amount) / maxAsk) * 100 : 0}
          >
            <PriceCell isAsk>{formatPrice(level.price)}</PriceCell>
            <AmountCell>{formatAmountShort(level.amount)}</AmountCell>
          </AskRow>
        ))}
      </OrderBookTable>

      <CurrentPriceWrap>
        <CurrentPriceValue>{currentPriceNum.toFixed(4)}</CurrentPriceValue>
      </CurrentPriceWrap>

      <OrderBookTable>
        {bids.map((level, i) => (
          <BidRow
            key={`bid-${level.price}-${i}`}
            depthPercent={maxBid > 0 ? (parseFloat(level.amount) / maxBid) * 100 : 0}
          >
            <PriceCell>{formatPrice(level.price)}</PriceCell>
            <AmountCell>{formatAmountShort(level.amount)}</AmountCell>
          </BidRow>
        ))}
      </OrderBookTable>

      <VolumeBarWrap>
        <VolumeBarBuy percent={data.buyPercent} />
        <VolumeBarSell percent={data.sellPercent} />
      </VolumeBarWrap>
      <VolumeBarLegend>
        <span>{t('orderBookBuy')} {data.buyPercent}%</span>
        <span>{data.sellPercent}% {t('orderBookSell')}</span>
      </VolumeBarLegend>
    </OrderBookWrap>
  )
}

interface OrderBookViewProps {
  /** 父组件递增此值时会触发订单簿刷新（如挂单/取消/改价成功后） */
  refreshTrigger?: number
}

export default function OrderBookView({ refreshTrigger }: OrderBookViewProps) {
  const { t } = useTranslation()
  const { data, loading, lastUpdated, refresh } = useOrderBookDepth(false)
  const [updatedAgo, setUpdatedAgo] = useState('')

  useEffect(() => {
    if (refreshTrigger != null && refreshTrigger > 0) refresh()
  }, [refreshTrigger, refresh])

  useEffect(() => {
    const tick = () => {
      if (!lastUpdated) {
        setUpdatedAgo(t('orderPriceUpdatedSeconds', { count: '0.5' }))
        return
      }
      const sec = (Date.now() - lastUpdated) / 1000
      if (sec < 60) {
        const val = Math.max(0.5, Math.floor(sec / 0.5) * 0.5).toFixed(1)
        setUpdatedAgo(t('orderPriceUpdatedSeconds', { count: val }))
      } else {
        setUpdatedAgo(t('orderPriceUpdatedMinutes', { count: Math.floor(sec / 60) }))
      }
    }
    tick()
    const id = setInterval(tick, 500)
    return () => clearInterval(id)
  }, [t, lastUpdated])

  return (
    <div style={{ padding: '0 0.75rem 0.75rem' }}>
      <TitleRow>
        <SectionTitle style={{ margin: 0 }}>{t('orderBookTitle')}({updatedAgo})</SectionTitle>
        <RefreshBtn type="button" onClick={refresh} disabled={loading} title={t('orderRefresh')} aria-label={t('orderRefresh')}>
          <RefreshCw size={14} />
        </RefreshBtn>
      </TitleRow>
      {loading && !data ? (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text2)' }}>{t('loading')}</div>
      ) : data && (data.asks.length > 0 || data.bids.length > 0) ? (
        <OrderBookContent data={data} />
      ) : (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text2)' }}>{t('orderNoOrders')}</div>
      )}
    </div>
  )
}
